/**
 * The four areas of the app, left to right in the switch at the top of the
 * sidebar: Strategy → Priorities → Plans → Action (strategic intent becomes
 * priorities, priorities become plans, and the plan in force is lived out and
 * changed). Each area has its own menu; Home, Apps, Notifications, Tasks and
 * Search sit above every menu, and Reports, Decisions and Settings below it.
 *
 * URLs did not move. The area is read from the path; pages every area shares
 * (notifications, reports, settings…) keep the area you came from, and the
 * pages Plans and Action both use (a plan, its changes) do the same.
 */
import { useEffect, useSyncExternalStore } from 'react';

export type AppArea = 'strategy' | 'priorities' | 'plans' | 'action';

/** The names in the switch. Change a word here and it changes everywhere. */
export const AREA_LABEL: Record<AppArea, string> = {
  strategy: 'Strategy',
  priorities: 'Priorities',
  plans: 'Plans',
  action: 'Action',
};

export const AREA_TITLE: Record<AppArea, string> = {
  strategy: 'Strategic intent — strategies, outcomes, goals and roadmaps',
  priorities: 'What matters — intake, items and ranking',
  plans: 'The next plan — preparing, testing and authorizing it',
  action: 'The plan in force — living it out and dealing with change',
};

/** Where each area opens. Action opens the plan in force (the primary plan). */
export const AREA_HOME: Record<AppArea, string> = {
  strategy: '/prioritization/strategy',
  priorities: '/prioritization/dashboard',
  plans: '/planning',
  action: '/planning/plans',
};

const STRATEGY_PREFIXES = [
  '/prioritization/strategy',
  '/prioritization/outcome-drivers',
  '/prioritization/desired-outcomes',
  '/prioritization/roadmap',
  '/prioritization/backplan',
  '/prioritization/open-questions',
];

const ACTION_PREFIXES = ['/prioritization/monitor'];

/** Pages Plans and Action both show: a plan, and the changes to it. */
const PLANS_OR_ACTION_PREFIXES = ['/planning/plans', '/planning/plan-of-record', '/planning/changes'];

/** Pages every area shares: they keep the area you came from. */
const SHARED_PREFIXES = [
  '/prioritization/dashboard',
  '/prioritization/apps',
  '/prioritization/notifications',
  '/prioritization/my-work',
  '/prioritization/my-tasks',
  '/prioritization/search',
  '/prioritization/reports',
  '/prioritization/decisions',
  '/prioritization/decision-intelligence',
  '/prioritization/calibration',
  '/prioritization/insights',
  '/prioritization/wristband',
  '/prioritization/settings',
];

const under = (path: string, prefixes: string[]) => prefixes.some((p) => path === p || path.startsWith(`${p}/`));

export function areaForPath(pathname: string, remembered: AppArea | null): AppArea {
  if (under(pathname, STRATEGY_PREFIXES)) return 'strategy';
  if (under(pathname, ACTION_PREFIXES)) return 'action';
  if (under(pathname, PLANS_OR_ACTION_PREFIXES)) return remembered === 'action' ? 'action' : 'plans';
  if (pathname.startsWith('/planning')) return 'plans';
  if (under(pathname, SHARED_PREFIXES)) return remembered ?? 'priorities';
  return 'priorities';
}

// ── The area you were last in (this tab only) ──
const KEY = 'pp_app_area';
const listeners = new Set<() => void>();

function read(): AppArea | null {
  try {
    const v = sessionStorage.getItem(KEY);
    return v === 'strategy' || v === 'priorities' || v === 'plans' || v === 'action' ? v : null;
  } catch {
    return null;
  }
}

export function rememberArea(area: AppArea): void {
  try {
    sessionStorage.setItem(KEY, area);
  } catch {
    /* private window: the path still decides */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Does this page belong to one area, so arriving on it makes that area the remembered
 * one? Not a shared page, not a page Plans and Action both show, and not an address
 * outside the two apps or a bare app root (`/`, `/prioritization`, the sign-in
 * redirect): landing there after signing in must not choose Priorities for you.
 */
export function pageOwnsArea(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/prioritization' || path === '/planning') return false;
  if (!under(path, ['/prioritization', '/planning'])) return false;
  return !under(path, SHARED_PREFIXES) && !under(path, PLANS_OR_ACTION_PREFIXES);
}

/**
 * The area the switch marks as chosen, or none: a shared page (Home, Settings, …)
 * with no area chosen yet in this tab marks none (owner ruling 2026-11-03: on Home
 * after signing in, none of the four is selected).
 */
export function selectedAreaFor(pathname: string, remembered: AppArea | null): AppArea | null {
  if (remembered === null && under(pathname, SHARED_PREFIXES)) return null;
  return areaForPath(pathname, remembered);
}

/** The area for this path, remembering it so shared pages keep it. */
export function useAppArea(pathname: string): AppArea {
  const remembered = useSyncExternalStore(subscribe, read, () => null);
  const area = areaForPath(pathname, remembered);
  const owned = pageOwnsArea(pathname);
  // A page that belongs to one area makes it the remembered one — once, when you arrive,
  // so a click on another area is not undone by the page you are leaving.
  useEffect(() => {
    if (owned && read() !== area) rememberArea(area);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return area;
}

/** The switch's chosen area for this path, or null when none is chosen yet (see selectedAreaFor). */
export function useSelectedArea(pathname: string): AppArea | null {
  useAppArea(pathname); // remembers the area of a page that owns one
  const remembered = useSyncExternalStore(subscribe, read, () => null);
  return selectedAreaFor(pathname, remembered);
}
